import { SortTH, Table } from 'foundations-components/transitional/components/Table';
import styled from 'foundations-theming-specialty/utils';
import { TicketListEmptyState } from './TicketListEmptyState';
import { useOnDelayedHover } from '../utils/useOnDelayedHover';
import { useTicketListingsColumns } from '../hooks/useTicketListingsColumns';
import { flexRender, getCoreRowModel, useReactTable } from '@tanstack/react-table';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useLocalStorageJSON, LocalStorageKey } from '../utils/useLocalStorageJSON';
import { ScrollContainer } from './atoms/ScrollContainer';
import { DEFAULT_SORT_VALUE } from '../constants/filters';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
import { createElement as _createElement } from "react";
const Wrapper = styled.div.withConfig({
  displayName: "TicketListingsTable__Wrapper"
})(["border-radius:", ";border:1px solid ", ";overflow:hidden;table{table-layout:fixed;width:100%;}th{position:relative;border-block-start:0px !important;white-space:nowrap;overflow:hidden;text-overflow:ellipsis;color:", ";}th:hover{background-color:", ";}tbody tr{background-color:", ";color:", ";}tbody tr:hover{cursor:pointer;color:", ";}th:active{background-color:", ";}tr:hover{cursor:pointer;}"], ({
  theme,
  isUsingPillBorderRadius
}) => isUsingPillBorderRadius ? theme.borderRadius[0] : theme.borderRadius[300], ({
  theme
}) => theme.color['border-core-subtle'], ({
  theme
}) => theme.color['text-core-on-fill-default'], ({
  theme
}) => theme.color['specialty-table-header-hover'], ({
  theme
}) => theme.color['fill-surface-default'], ({
  theme
}) => theme.color['text-core-default'], ({
  theme
}) => theme.color['text-core-subtle'], ({
  theme
}) => theme.color['specialty-table-header-pressed']);
const ResizeHandle = styled.div.withConfig({
  displayName: "TicketListingsTable__ResizeHandle"
})(["position:absolute;top:0;right:0px;z-index:10;height:calc(100% - 8px);cursor:col-resize;width:8px;margin:4px 2px;opacity:0;:hover{opacity:1;}&::after{content:'';position:absolute;top:0;left:0;width:calc(100% - 4px);height:calc(100%);background-color:", ";border-radius:", ";}:active&::after{background-color:", ";}"], ({
  theme
}) => theme.color['border-interactive-default'], ({
  theme
}) => theme.borderRadius.full, ({
  theme
}) => theme.color['border-interactive-pressed']);
export function TicketListingsTable({
  data,
  isLoading,
  sort,
  setSort,
  onRowClick,
  onRowHover,
  isUsingPillBorderRadius,
  error,
  hasActiveFilters
}) {
  const [isResizing, setIsResizing] = useState(false);
  const [savedColumnSizing, setSavedColumnSizing] = useLocalStorageJSON(LocalStorageKey.TICKET_LISTINGS_COLUMN_SIZING, {
    subject: 300
  });
  const columns = useTicketListingsColumns(isLoading);
  const [columnSizing, setColumnSizing] = useState(savedColumnSizing);
  const tableRowProps = useOnDelayedHover(id => onRowHover(Number(id)), 100, isLoading);
  const table = useReactTable({
    data,
    columns,
    state: {
      columnSizing
    },
    defaultColumn: {
      size: 150
    },
    onColumnSizingChange: setColumnSizing,
    getCoreRowModel: getCoreRowModel(),
    columnResizeMode: 'onChange'
  });
  useEffect(() => {
    const id = setTimeout(() => {
      setSavedColumnSizing(columnSizing);
    }, 1000);
    return () => clearTimeout(id);
  }, [columnSizing, setSavedColumnSizing]);
  const isCurrentlyResizing = table.getHeaderGroups().some(headerGroup => headerGroup.headers.some(header => header.column.getIsResizing()));

  // This is a workaround to prevent the sort from being changed while resizing
  // it overcomes some of the weird SortTH behavior with onSortChange
  useEffect(() => {
    const id = setTimeout(() => {
      setIsResizing(isCurrentlyResizing);
    });
    return () => clearTimeout(id);
  }, [isCurrentlyResizing]);
  const sorting = useMemo(() => {
    const map = {};
    for (const field of sort) {
      map[field.propertyName] = field.direction === 'ASCENDING' ? 'ascending' : 'descending';
    }
    return map;
  }, [sort]);
  const onSortChange = useCallback((value, id) => {
    if (isResizing) return;
    if (sorting[id] === 'ascending') {
      setSort(DEFAULT_SORT_VALUE);
    } else {
      setSort([{
        propertyName: id,
        direction: value === 'ascending' ? 'ASCENDING' : 'DESCENDING'
      }]);
    }
  }, [setSort, sorting, isResizing]);
  return /*#__PURE__*/_jsx(Wrapper, {
    isUsingPillBorderRadius: isUsingPillBorderRadius,
    children: /*#__PURE__*/_jsxs(Table, {
      "data-test-id": "ticket-listings-table",
      bordered: false,
      condensed: true,
      flush: true,
      ScrollContainer: ScrollContainer,
      children: [/*#__PURE__*/_jsx("thead", {
        children: table.getHeaderGroups().map(headerGroup => /*#__PURE__*/_jsx("tr", {
          children: headerGroup.headers.map(header => {
            var _sorting$header$colum;
            return /*#__PURE__*/_jsxs(SortTH, {
              "data-test-id": `sort-header-${header.column.id}`,
              truncate: true,
              inlineSize: `${header.getSize()}px`,
              sort: (_sorting$header$colum = sorting[header.column.id]) !== null && _sorting$header$colum !== void 0 ? _sorting$header$colum : 'none',
              onSortChange: s => onSortChange(s.currentTarget.value, header.column.id),
              children: [flexRender(header.column.columnDef.header, header.getContext()), /*#__PURE__*/_jsx(ResizeHandle, {
                onMouseDown: header.getResizeHandler(),
                onTouchStart: header.getResizeHandler(),
                onDoubleClick: () => header.column.resetSize()
              })]
            }, header.column.id);
          })
        }, headerGroup.id))
      }), /*#__PURE__*/_jsx("tbody", {
        children: table.getRowModel().rows.length ? table.getRowModel().rows.map(row => /*#__PURE__*/_createElement("tr", Object.assign({}, tableRowProps, {
          id: row.original.hs_ticket_id.toString(),
          key: row.original.hs_ticket_id,
          onClick: () => onRowClick(row.original.hs_ticket_id),
          role: "link",
          tabIndex: 0,
          onKeyDown: e => {
            if (e.target !== e.currentTarget) return;
            if (e.key === 'Enter' || e.key === ' ') {
              e.preventDefault();
              onRowClick(row.original.hs_ticket_id);
            }
          }
        }), row.getVisibleCells().map(cell => {
          const {
            column
          } = cell;
          const {
            header
          } = column.columnDef;
          return /*#__PURE__*/_jsx("td", {
            "data-title": header,
            children: flexRender(cell.column.columnDef.cell, cell.getContext())
          }, cell.id);
        }))) : /*#__PURE__*/_jsx("tr", {
          children: /*#__PURE__*/_jsx("td", {
            colSpan: table.getVisibleLeafColumns().length,
            children: /*#__PURE__*/_jsx(TicketListEmptyState, {
              error: error,
              hasActiveFilters: hasActiveFilters
            })
          })
        })
      })]
    })
  });
}